import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Calendar as CalendarIcon, Check } from "lucide-react";
import {
  MONTH_NAMES, periodPresets, presetToPeriod, weeksInMonth,
  weekPreset, monthPreset, customPeriod, formatRange,
} from "@/lib/periods";
import { cn } from "@/lib/utils";

export function PeriodPicker({ value, onChange, testid = "period-picker" }) {
  const [open, setOpen] = useState(false);
  const [customStart, setCustomStart] = useState(value?.start_date || "");
  const [customEnd, setCustomEnd] = useState(value?.end_date || "");
  // Stable "now" per mount so presets don't recompute on every render.
  const [now] = useState(() => new Date());
  const nowYear = now.getFullYear();
  const nowMonth = now.getMonth();
  const [monthYear, setMonthYear] = useState(String(nowYear));
  const [monthIdx, setMonthIdx] = useState(String(nowMonth));

  const presets = useMemo(() => periodPresets(now), [now]);
  const weeks = useMemo(() => weeksInMonth(nowYear, nowMonth), [nowYear, nowMonth]);

  const pick = (p) => {
    onChange(p);
    setOpen(false);
  };

  const yearsList = useMemo(() => {
    const arr = [];
    for (let y = nowYear; y >= nowYear - 4; y--) arr.push(y);
    return arr;
  }, [nowYear]);

  const applyCustom = () => {
    if (!customStart || !customEnd) return;
    pick(customPeriod(customStart, customEnd));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          data-testid={`${testid}-trigger`}
          className="justify-start gap-2 whitespace-nowrap"
        >
          <CalendarIcon className="h-4 w-4" strokeWidth={1.75} />
          <span className="max-w-[24rem] truncate">
            {value?.label || "Select period"}
          </span>
          {value && (
            <span className="ml-1 hidden text-[10px] uppercase tracking-widest text-muted-foreground sm:inline">
              {formatRange(value.start_date, value.end_date)}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[min(720px,calc(100vw-2rem))] p-0" data-testid={`${testid}-panel`}>
        <div className="grid grid-cols-1 gap-0 md:grid-cols-3">
          {/* Quick presets */}
          <div className="border-b border-border p-4 md:border-b-0 md:border-r">
            <div className="label-caps mb-3">Quick</div>
            <div className="grid grid-cols-2 gap-1.5 md:grid-cols-1">
              {["today", "yesterday", "this_week", "last_week", "this_month", "last_month", "this_quarter", "this_year", "financial_year"].map((k) => {
                const p = presetToPeriod(presets[k]);
                const active = value?.key === p.key;
                return (
                  <button
                    key={k}
                    data-testid={`${testid}-preset-${k}`}
                    onClick={() => pick(p)}
                    className={cn(
                      "flex items-center justify-between rounded-md px-2.5 py-1.5 text-left text-sm transition-colors",
                      active ? "bg-primary/10 text-primary" : "hover:bg-muted",
                    )}
                  >
                    <span>{p.label}</span>
                    {active && <Check className="h-3.5 w-3.5" />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Weeks of the current month */}
          <div className="border-b border-border p-4 md:border-b-0 md:border-r">
            <div className="label-caps mb-3">Weeks of {MONTH_NAMES[nowMonth]}</div>
            <div className="space-y-1.5">
              {weeks.map((w) => {
                const p = weekPreset(w);
                const active = value?.key === p.key;
                return (
                  <button
                    key={w.n}
                    data-testid={`${testid}-week-${w.n}`}
                    onClick={() => pick(p)}
                    className={cn(
                      "flex w-full items-center justify-between gap-2 rounded-md px-2.5 py-1.5 text-left text-sm transition-colors",
                      active ? "bg-primary/10 text-primary" : "hover:bg-muted",
                    )}
                  >
                    <span className="font-medium">Week {w.n}</span>
                    <span className="text-[11px] text-muted-foreground tabular-nums">
                      {w.start.getDate()}/{w.start.getMonth() + 1} – {w.end.getDate()}/{w.end.getMonth() + 1}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Months + Custom range */}
          <div className="space-y-4 p-4">
            <div>
              <div className="label-caps mb-3">Month</div>
              <div className="grid grid-cols-2 gap-2">
                <Select value={monthYear} onValueChange={setMonthYear}>
                  <SelectTrigger className="h-8 text-xs" data-testid={`${testid}-month-year`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {yearsList.map((y) => (<SelectItem key={y} value={String(y)}>{y}</SelectItem>))}
                  </SelectContent>
                </Select>
                <Select value={monthIdx} onValueChange={setMonthIdx}>
                  <SelectTrigger className="h-8 text-xs" data-testid={`${testid}-month-idx`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTH_NAMES.map((m, i) => (<SelectItem key={m} value={String(i)}>{m}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                variant="secondary"
                size="sm"
                className="mt-2 w-full"
                data-testid={`${testid}-apply-month`}
                onClick={() => pick(monthPreset(Number(monthYear), Number(monthIdx)))}
              >
                Apply {MONTH_NAMES[Number(monthIdx)]} {monthYear}
              </Button>
            </div>

            <div>
              <div className="label-caps mb-3">Custom Range</div>
              <div className="space-y-2">
                <div className="space-y-1">
                  <Label className="text-[11px] text-muted-foreground">Start</Label>
                  <Input
                    type="date"
                    value={customStart}
                    data-testid={`${testid}-custom-start`}
                    onChange={(e) => setCustomStart(e.target.value)}
                    className="h-8"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px] text-muted-foreground">End</Label>
                  <Input
                    type="date"
                    value={customEnd}
                    data-testid={`${testid}-custom-end`}
                    onChange={(e) => setCustomEnd(e.target.value)}
                    className="h-8"
                  />
                </div>
                <Button
                  size="sm"
                  className="w-full"
                  data-testid={`${testid}-apply-custom`}
                  disabled={!customStart || !customEnd}
                  onClick={applyCustom}
                >
                  Apply Custom Range
                </Button>
              </div>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
